<template>
  <div>
    <setting-header
      :breadcrumbs="[{ name: '各種設定' }]"
      title="いいね自動送信設定"
    />
    <section class="margin-top-05">
      <div class="grid-x align-justify">
        <div class="medium-6 cell">
          <p>求人ごとにいいねの自動送信を設定します。</p>
        </div>
      </div>
      <table class="unstriped hover boss-table">
        <thead>
          <tr>
            <th class="boss-table-head-center">
              求人ID
            </th>
            <th class="boss-table-head-center">
              管理用求人名
            </th>
            <th class="boss-table-head-center">
              対象者を<br>変更・確認
            </th>
            <th class="boss-table-head-center">
              送信対象者
            </th>
            <th class="boss-table-head-center">
              1日当たりの<br>最大送信数
            </th>
            <th class="boss-table-head-center auto-liked-setting-enable-th">
              ON／OFF
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-if="!isEmptyJob">
            <tr
              v-for="(autoLikedSetting, i) in autoLikedSettings"
              :key="`job-${ i }`"
            >
              <td
                class="boss-table-data pega-width-78-px"
                data-test="job-id"
              >
                {{ autoLikedSetting.jobId }}
              </td>
              <td
                class="boss-table-data pega-min-width-6-25-rem"
                data-test="job-management-name"
              >
                {{ autoLikedSetting.jobManagementName }}
              </td>
              <td class="boss-table-data pega-width-120-px">
                <button
                  class="boss-button pega-button margin-03"
                  data-test="auto-liked-setting-condition-button"
                  :disabled="!isSendScout"
                  @click="routeSettingAutoLikedSearchCondition(autoLikedSetting)"
                >
                  変更・確認
                </button>
              </td>
              <td
                class="boss-table-data pega-conditions-font-size"
                data-test="auto-liked-setting-conditions"
              >
                <setting-auto-liked-condition
                  v-if="!!autoLikedSetting.conditions"
                  :condition="autoLikedSetting.conditions"
                />
                <template v-else>
                  未設定
                </template>
              </td>
              <td class="boss-table-data pega-width-100-px">
                <select
                  v-model="autoLikedSetting.maxSendCount"
                  data-test="auto-liked-setting-max-sent-count-select"
                  class="boss-input-select"
                  :disabled="!isSendScout"
                  @change="updateAutoLikedSettingMaxSendCount(autoLikedSetting.jobId, autoLikedSetting.maxSendCount)"
                >
                  <option
                    v-show="!autoLikedSetting.isEnabled"
                    :value="null"
                    data-test="max-send-count-null"
                  >
                    未設定
                  </option>
                  <option
                    v-for="maxSendCount in maxSendCounts"
                    :key="maxSendCount"
                    :value="maxSendCount"
                    :data-test="`max-send-count-${maxSendCount}`"
                  >
                    {{ maxSendCount }}
                  </option>
                </select>
              </td>
              <td class="boss-table-data pega-width-100-px">
                <div class="switch toggle-switch">
                  <input
                    :id="`auto-liked-setting-enable-${ autoLikedSetting.jobId }`"
                    v-model="autoLikedSetting.isEnabled"
                    class="toggle-input"
                    data-test="auto-liked-setting-enable-toggle-button"
                    type="checkbox"
                    :disabled="!isSendScout"
                    @change="updateAutoLikedSettingIsEnabled(autoLikedSetting, $event)"
                  >
                  <label
                    :for="`auto-liked-setting-enable-${ autoLikedSetting.jobId }`"
                    class="toggle-label"
                  />
                </div>
              </td>
            </tr>
          </template>
          <tr v-else>
            <td
              colspan="8"
              data-test="no-job-label"
            >
              ※現在掲載中の求人はありません
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SettingAutoLikedCondition from '@/components/organisms/SettingAutoLikedCondition.vue'
import SettingHeader from '@/components/atoms/SettingHeader'
import Indicator from '@/lib/indicator'

export default {
  components: { SettingAutoLikedCondition, SettingHeader },
  data () {
    return {
      autoLikedSettings: [],
      maxSendCounts: [5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
    }
  },
  computed: {
    ...mapGetters([
      'isSendScout'
    ]),
    isEmptyJob () {
      return !this.autoLikedSettings.length
    }
  },
  created () {
    this.fetchPublishingAutoLikedSettings()
  },
  methods: {
    fetchPublishingAutoLikedSettings () {
      Indicator.emitShow()
      this.$http.secured.get('/scout/view/company/auto_liked_settings').then(response => {
        this.$set(this, 'autoLikedSettings', response.data)
      }).finally(() => {
        Indicator.emitHide()
      })
    },
    updateAutoLikedSettingMaxSendCount (jobId, maxSendCount) {
      this.$http.secured.put(`/scout/message/send/auto_liked_settings/jobs/${jobId}/_max_send_count`, { maxSendCount: maxSendCount }
      ).then(() => {
        this.$toasted.global.success({ message: '1日当たりの最大送信数を更新しました。' })
      })
    },
    updateAutoLikedSettingIsEnabled (autoLikedSetting, event) {
      const afterCheckboxVal = event.target.checked
      if (!autoLikedSetting.conditions) {
        this.$toasted.global.error({ message: '送信対象者が未設定です' })
        event.target.checked = false
        this.$set(autoLikedSetting, 'isEnabled', false)
        return
      }
      if (!autoLikedSetting.maxSendCount) {
        this.$toasted.global.error({ message: '1日当たりの最大送信数が未設定です' })
        event.target.checked = false
        this.$set(autoLikedSetting, 'isEnabled', false)
        return
      }
      this.$http.secured.put('/scout/message/send/auto_liked_settings/_is_enabled', { autoLikedId: autoLikedSetting.autoLikedId, isEnabled: afterCheckboxVal }
      ).then(() => {
        this.$toasted.global.success({ message: `いいね自動送信を${afterCheckboxVal ? 'ON' : 'OFF'}にしました。` })
      }).catch(() => {
        event.target.checked = !afterCheckboxVal
        this.$set(autoLikedSetting, 'isEnabled', !afterCheckboxVal)
      })
    },
    routeSettingAutoLikedSearchCondition (autoLikedSetting) {
      if (!!autoLikedSetting.conditions && !!autoLikedSetting.conditions.id) {
        this.$router.push({ name: 'settingAutoLikedSearchEdit', params: { jobId: autoLikedSetting.jobId, conditionId: autoLikedSetting.conditions.id } })
      } else {
        this.$router.push({ name: 'settingAutoLikedSearchRegister', params: { jobId: autoLikedSetting.jobId } })
      }
    }
  }
}
</script>
